import { useState } from 'react';
import { Button, Flex, Tabs } from '@mantine/core';
import { DrawnSignatureSource } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { SignaturePad } from '../SignaturePad/SignaturePad';
import { SignatureGenerator } from '../SignatureGenerator/SignatureGenerator';
import { SignatureUploader } from '../SignatureUploader/SignatureUploader';
import { useSignatureEditor } from './useSignatureEditor';
import { SignatureEditorTab } from '@dealstack/frontend/shared/components/Signature/SignatureEditor/useSignatureEditor.types';
import { isSignatureEditorTab } from '@dealstack/frontend/shared/components/Signature/SignatureEditor/SignatureEditor.utils';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	confirmLabel: string;
	onConfirm: (dataUrl: string, source: DrawnSignatureSource) => void | Promise<void>;
	isBusy?: boolean;
}

export const SignatureEditor = ({ confirmLabel, onConfirm, isBusy = false }: Props) => {
	const editor = useSignatureEditor();
	const [isConfirming, setIsConfirming] = useState(false);

	const handleConfirm = async () => {
		setIsConfirming(true);
		try {
			const dataUrl = await editor.getSignatureImageUrl();
			if (!dataUrl) {
				return;
			}
			// Type tab produces a synthetic script-font rendering — flagged as
			// `typed` so the certificate re-typesets the name instead of
			// embedding the PNG. Draw and Upload produce real bitmap content.
			const source =
				editor.activeTab === SignatureEditorTab.TYPE
					? DrawnSignatureSource.TYPED
					: DrawnSignatureSource.CANVAS;
			await onConfirm(dataUrl, source);
		} finally {
			setIsConfirming(false);
		}
	};

	const confirmDisabled = !editor.hasContent() || isConfirming || isBusy;

	return (
		<Flex direction="column" gap="md" w="100%">
			<Tabs
				value={editor.activeTab}
				onChange={(v) => {
					if (isSignatureEditorTab(v)) {
						editor.setActiveTab(v);
					}
				}}
			>
				<Tabs.List>
					<Tabs.Tab value={SignatureEditorTab.DRAW}>Draw</Tabs.Tab>
					<Tabs.Tab value={SignatureEditorTab.TYPE}>Type</Tabs.Tab>
					<Tabs.Tab value={SignatureEditorTab.UPLOAD}>Upload</Tabs.Tab>
				</Tabs.List>
				<Tabs.Panel value={SignatureEditorTab.DRAW} pt="md">
					<SignaturePad
						controller={editor.padController}
						signatureCanvasProps={{ canvasProps: { height: 200 } }}
					/>
				</Tabs.Panel>
				<Tabs.Panel value={SignatureEditorTab.TYPE} pt="md">
					<SignatureGenerator controller={editor.generatorController} />
				</Tabs.Panel>
				<Tabs.Panel value={SignatureEditorTab.UPLOAD} pt="md">
					<SignatureUploader controller={editor.uploaderController} />
				</Tabs.Panel>
			</Tabs>
			<Flex justify="space-between">
				<Button variant="subtle" onClick={editor.clear} disabled={!editor.hasContent()}>
					Clear
				</Button>
				<Button
					variant="filled"
					onClick={handleConfirm}
					disabled={confirmDisabled}
					loading={isBusy}
					data-testid={TEST_ELEMENT_IDS.EID_EASY_SIGNATURE_CONFIRM_BUTTON}
				>
					{confirmLabel}
				</Button>
			</Flex>
		</Flex>
	);
};
